import React from 'react';
import { FormattedMessage } from 'react-intl';
import { apiRequest } from '../actions/apiActions';
import { showLoading, hideLoading, addUiMsg } from '../actions/uiActions';
import { apiUrls, apiVersion } from '../../apiUrls';
import {
  ASSOCIATE_USERS_UNIT,
  ASSOCIATE_USER_UNITS,
  USER_UNIT_ERROR,
} from '../actions/userUnitActions';
import { PROCESS_EDIT_USER } from '../actions/userActions';
import { userText } from '../../components/shared/SharedInternationalizations';

const wasNotUpdatedText = (
  <FormattedMessage
    id="userUnitMiddleware.wasNotUpdatedText"
    defaultMessage="was created."
    description="The last part of the sentence 'Unit: 'Room 101' was not updated'"
  />
);

/**
 * Middleware to trigger an api call to add a new unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to create the unit
 */
export const associateUsersToUnit = (store) => (next) => (action) => {
  next(action);
  if (action.type === ASSOCIATE_USERS_UNIT) {
    const users = action.payload.usersId;
    const newUnit = action.payload.unit;
    const initialUsers = action.payload.initialUsers;

    users.map((userId) => {
      const userInfo = store.getState().users.users[userId];
      const unitArray = [...userInfo.units];
      let unitsChanged = false;
      if (!unitArray.includes(newUnit)) {
        unitArray.push(newUnit);
        unitsChanged = true;
      }
      const body = {
        units: unitArray,
      };

      const meta = {
        method: 'PATCH',
        url:
          apiUrls.baseUrl +
          apiVersion +
          apiUrls[apiVersion].user +
          userId +
          '/',
        onSuccess: PROCESS_EDIT_USER,
        onError: USER_UNIT_ERROR,
        auth: store.getState().auth.token,
        body: true,
        navigate: action.navigate,
        previous: action.previous,
      };
      if (unitsChanged) {
        store.dispatch(showLoading());
        store.dispatch(apiRequest(body, meta));
      }
      return '';
    });

    initialUsers.map((userId) => {
      //this part of the code is getting called for the users who were originally assigned
      if (!users.includes(userId)) {
        //if we get to this point, the user has been unassigned and we need to remove the association
        const userInfo = store.getState().users.users[userId];
        const unitArray = [...userInfo.units];
        const activeUnit = userInfo.active_unit;
        let updatedUnits = [];
        
        if (unitArray.includes(newUnit)) {
          updatedUnits = unitArray.filter((u) => u !== newUnit);
        }

        //the units that the user is assigned to now to send to API
        let body = {
          units: updatedUnits
        };

        //checking to see if the users active unit was also this unit.
        //If it was, clear the active unit as well since the user is not assigned to this unit now
        if (activeUnit && activeUnit === newUnit){
          body.active_unit = null;
        }

        const meta = {
          method: 'PATCH',
          url:
            apiUrls.baseUrl +
            apiVersion +
            apiUrls[apiVersion].user +
            userId +
            '/',
          onSuccess: PROCESS_EDIT_USER,
          onError: USER_UNIT_ERROR,
          auth: store.getState().auth.token,
          body: true,
          navigate: action.navigate,
          previous: action.previous,
        };

        store.dispatch(showLoading());
        store.dispatch(apiRequest(body, meta));
        return '';
      }
      return '';
    });
  }
};

/**
 * Middleware to trigger an api call to add a new unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to create the unit
 */
export const associateUnitsToUser = (store) => (next) => (action) => {
  next(action);
  if (action.type === ASSOCIATE_USER_UNITS) {
    const user = action.payload.user;
    const updatedUnits = action.payload.unitsId;
    const active = action.payload.active_unit;
    const userInfo = store.getState().users.users[user];

    const filteredUnits = [...updatedUnits];

    let activeUnit = active;
    if ((userInfo.active_unit && !active) || (active && !updatedUnits.includes(parseInt(active)))) {
      activeUnit = '';
    }

    const body = {
      units: filteredUnits,
      active_unit: activeUnit,
    };
    const meta = {
      method: 'PATCH',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].user + user + '/',
      onSuccess: PROCESS_EDIT_USER,
      onError: USER_UNIT_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      previous: action.previous,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));

    return '';
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const userUnitError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === USER_UNIT_ERROR) {
    dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        {userText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.originalPayload.name}"
        </span>
        {wasNotUpdatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      error: action.payload.name[0],
      messageType: 'danger',
    };
    dispatch(addUiMsg(message));
    console.error(action.payload);
  }
};

export const userUnitMdl = [
  associateUsersToUnit,
  associateUnitsToUser,
  userUnitError,
];
